import React from "react";
import {
  DragDropContext,
  DropResult,
  DragStart,
  DragUpdate,
} from "@hello-pangea/dnd";
import { Task, TaskStatus, Project } from "../../types/Work";
import { KanbanColumn } from "./KanbanColumn";

interface KanbanBoardProps {
  tasks: Task[];
  projects?: Project[];
  onTaskStatusChange: (taskId: string, newStatus: TaskStatus) => void;
  onTaskReorder: (
    taskId: string,
    newStatus: TaskStatus,
    newIndex: number,
  ) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  onToggleTimer: (task: Task) => void;
  onViewDoneHistory: () => void;
  focusMode?: boolean;
}

const COLUMNS: { id: TaskStatus; title: string; color: string }[] = [
  { id: "backlog", title: "Backlog", color: "#8e8e93" },
  { id: "todo", title: "To Do", color: "#007aff" },
  { id: "in_progress", title: "In Progress", color: "#ff9500" },
  { id: "in_review", title: "In Review", color: "#af52de" },
  { id: "done", title: "Done", color: "#34c759" },
];

const DEFAULT_VISIBLE_COLUMNS: TaskStatus[] = [
  "in_progress", "in_review", "done",
];

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  tasks,
  projects,
  onTaskStatusChange,
  onTaskReorder,
  onEditTask,
  onDeleteTask,
  onToggleTimer,
  onViewDoneHistory,
  focusMode = false,
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [selectedColumns, setSelectedColumns] = React.useState<TaskStatus[]>(DEFAULT_VISIBLE_COLUMNS);

  const toggleColumn = (id: TaskStatus) => {
    setSelectedColumns((current) => {
      const next = current.includes(id)
        ? current.length > 1 ? current.filter((column) => column !== id) : current
        : COLUMNS.filter((column) => current.includes(column.id) || column.id === id).map((column) => column.id);
      return next;
    });
  };

  const handleDragStart = (result: DragStart) => {
    setIsDragging(true);
  };

  const handleDragUpdate = (update: DragUpdate) => {
    // Add visual feedback during drag if needed
  };

  const handleDragEnd = (result: DropResult) => {
    setIsDragging(false);

    const { destination, source, draggableId } = result;

    // Dropped outside a valid droppable
    if (!destination) {
      return;
    }

    // Dropped in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newStatus = destination.droppableId as TaskStatus;

    // If moving to a different column, use the simple status change
    if (source.droppableId !== destination.droppableId) {
      onTaskStatusChange(draggableId, newStatus);
    } else {
      // Reordering within the same column
      onTaskReorder(draggableId, newStatus, destination.index);
    }
  };;

  const getTasksByStatus = (status: TaskStatus) => {
    const statusTasks = tasks
      .filter((task) => task.status === status)
      .sort((a, b) =>
        status === "done"
          ? (b.completedAt ?? b.updatedAt).getTime() -
            (a.completedAt ?? a.updatedAt).getTime()
          : (a.order ?? 0) - (b.order ?? 0),
      );
    return status === "done" ? statusTasks.slice(0, 5) : statusTasks;
  };

  const visibleColumns = focusMode
    ? COLUMNS.filter(
        (column) => column.id === "todo" || column.id === "in_progress",
      )
    : COLUMNS.filter((column) => selectedColumns.includes(column.id));

  return (
    <DragDropContext
      onDragStart={handleDragStart}
      onDragUpdate={handleDragUpdate}
      onDragEnd={handleDragEnd}
    >
      {!focusMode && (
        <div className="kanban-column-picker" aria-label="Choose visible columns">
          <div className="kanban-column-picker-heading">
            <i className="fas fa-table-columns"></i>
            <span>Columns</span>
            <span className="kanban-column-picker-count">{selectedColumns.length} of {COLUMNS.length} shown</span>
          </div>
          <div className="kanban-column-picker-options">
            {COLUMNS.map((column) => {
              const isSelected = selectedColumns.includes(column.id);
              return (
                <button
                  key={column.id}
                  type="button"
                  className={`kanban-column-picker-option ${isSelected ? "is-selected" : ""}`}
                  aria-pressed={isSelected}
                  disabled={isSelected && selectedColumns.length === 1}
                  title={isSelected ? `Hide ${column.title}` : `Show ${column.title}`}
                  onClick={() => toggleColumn(column.id)}
                >
                  <span className="kanban-column-picker-dot" style={{ backgroundColor: column.color }}></span>
                  {column.title}
                  <span className="kanban-column-picker-task-count">
                    {tasks.filter((task) => task.status === column.id).length}
                  </span>
                  <i className={`fas ${isSelected ? "fa-check" : "fa-plus"}`}></i>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className={`kanban-board ${isDragging ? "is-dragging" : ""}`}>
        {visibleColumns.map((column) => {
          const columnTasks = getTasksByStatus(column.id);
          const totalTaskCount = tasks.filter(
            (task) => task.status === column.id,
          ).length;
          return (
            <KanbanColumn
              key={column.id}
              id={column.id}
              title={column.title}
              tasks={columnTasks}
              projects={projects}
              onEditTask={onEditTask}
              onDeleteTask={onDeleteTask}
              onToggleTimer={onToggleTimer}
              onStatusChange={onTaskStatusChange}
              onViewAll={column.id === "done" ? onViewDoneHistory : undefined}
              totalTaskCount={totalTaskCount}
            />
          );
        })}
      </div>
    </DragDropContext>
  );
};
